import { ref, onMounted, onUnmounted } from 'vue'
import { toast } from 'vue3-toastify'

const currentVersion = ref<string | null>(null)
const updateAvailable = ref(false)
let checkInterval: ReturnType<typeof setInterval> | null = null

function isVersionDebugEnabled() {
  const params = new URLSearchParams(window.location.search)
  return params.get('zulu_debug') === '1'
}

function logVersionDebug(...args: unknown[]) {
  if (isVersionDebugEnabled()) {
    console.log('[AppVersion]', ...args)
  }
}

function warnVersionDebug(...args: unknown[]) {
  if (isVersionDebugEnabled()) {
    console.warn('[AppVersion]', ...args)
  }
}

function showUpdateToast(message: string) {
  toast.info(`${message} Use the Reload Page button in the header when you're ready.`, {
    position: 'top-right',
    autoClose: false,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
  })
}

/**
 * Check if a new version of the app is available.
 * Fetches /version.json (bypassing cache) and compares with current version.
 */
async function checkForUpdate() {
  try {
    logVersionDebug('Checking for update...')
    const response = await fetch('/version.json?t=' + Date.now(), {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    })
    if (!response.ok) {
      warnVersionDebug('version.json not found or not ok:', response.status)
      return
    }

    const data = await response.json()
    const serverVersion = data.version as string
    logVersionDebug('Server version:', serverVersion, 'Current:', currentVersion.value)

    if (!currentVersion.value) {
      logVersionDebug('No previous version, storing:', serverVersion)
      currentVersion.value = serverVersion
      return
    }

    if (serverVersion !== currentVersion.value) {
      logVersionDebug(`Update detected: ${currentVersion.value} -> ${serverVersion}`)
      updateAvailable.value = true
      showUpdateToast('A new version of RADSuite is available. Please refresh when convenient.')
      stopVersionChecking()
    }
  } catch (err) {
    warnVersionDebug('Failed to check version:', err)
  }
}

function startVersionChecking(intervalMs = 60000) {
  stopVersionChecking()
  // Initial check after a short delay
  setTimeout(checkForUpdate, 5000)
  // Then poll periodically
  checkInterval = setInterval(checkForUpdate, intervalMs)
}

function stopVersionChecking() {
  if (checkInterval) {
    clearInterval(checkInterval)
    checkInterval = null
  }
}

/**
 * Handle chunk load errors by suggesting a reload.
 * This catches the "Failed to fetch dynamically imported module" errors.
 */
function handleChunkError(error: ErrorEvent | PromiseRejectionEvent) {
  const message = error instanceof ErrorEvent ? error.message : String(error.reason)
  if (
    message?.includes('Failed to fetch dynamically imported module') ||
    message?.includes('Loading chunk') ||
    message?.includes('Loading CSS chunk')
  ) {
    warnVersionDebug('Chunk load error detected, suggesting reload:', message)
    updateAvailable.value = true
    showUpdateToast('RADSuite has been updated. Please refresh the page to continue.')
    stopVersionChecking()
    return true
  }
  return false
}

export function useAppVersion() {
  logVersionDebug('useAppVersion composable initialized')
  onMounted(() => {
    logVersionDebug('Component mounted, starting version checks...')
    startVersionChecking()
    window.addEventListener('error', handleChunkError)
    window.addEventListener('unhandledrejection', handleChunkError)
  })

  onUnmounted(() => {
    stopVersionChecking()
    window.removeEventListener('error', handleChunkError)
    window.removeEventListener('unhandledrejection', handleChunkError)
  })

  return {
    currentVersion,
    updateAvailable
  }
}
